// src/cockpit4/components/MarketingFunnel.tsx
// ============================================
// P1-功能4：营销漏斗分析与优化（营销中心）
//
// 功能：
//   1. 完整的 6 层营销漏斗可视化（曝光 → 点击 → 关注 → 互动 → 转化 → 复购）
//   2. 每个环节显示人数、转化率、流失人数、环比变化
//   3. 漏斗可视化（倒金字塔形状，使用 div + CSS 实现，每层宽度按人数递减）
//   4. AI 定位最大瓶颈环节（最低转化率环节红色高亮）
//   5. 针对性优化建议（基于瓶颈环节给出优化方案，参考行业 benchmark）
//   6. A/B 测试管理（创建测试、实时监控效果、自动判定优胜版本）
//
// 数据来源：
//   - useDashboardData 的 marketing.platforms（Record<MarketingPlatform, PlatformData>）
//   - PlatformData.metrics 包含: views / likes / favorites / comments / shares / dm / followersGain / followersTotal
//   - 漏斗阶段映射（从 metrics 推导，估算逻辑在注释中说明）：
//       * 曝光 Exposure = views
//       * 点击 Click = likes + comments + shares（互动行为视为点击）
//       * 关注 Follow = followersGain
//       * 互动 Interaction = comments + dm
//       * 转化 Conversion = followersGain * 0.3（简化估算：粉丝增长中的高意向比例）
//       * 复购 Repurchase = followersGain * 0.1（简化估算：粉丝增长中的复购比例）
//   - 漏斗自定义配置存 localStorage（key: xeaos_marketing_funnel_config）
//   - A/B 测试配置和结果存 localStorage（key: xeaos_marketing_ab_tests）
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Filter,
  RefreshCw,
  Loader2,
  TrendingDown,
  AlertTriangle,
  Lightbulb,
  Plus,
  X,
  Trash2,
  FlaskConical,
  ChevronDown,
  ChevronRight,
  Target,
  Calendar,
  Award,
} from "lucide-react";
import { useDashboardData } from "../../hooks/useDashboardData";
import type { MarketingPlatform } from "../../hooks/useDashboardData";

// ============================================
// 类型与常量
// ============================================

const FUNNEL_CONFIG_KEY = "xeaos_marketing_funnel_config";
const AB_TESTS_KEY = "xeaos_marketing_ab_tests";

/** 漏斗阶段标识 */
type FunnelStageId =
  | "exposure"
  | "click"
  | "follow"
  | "interaction"
  | "conversion"
  | "repurchase";

/** 时间范围 */
type TimeRange = "day" | "week" | "month";

/** 漏斗阶段定义（内置分析框架，非用户数据） */
interface FunnelStageDef {
  id: FunnelStageId;
  label: string;
  /** 阶段描述（用于 AI 优化建议生成） */
  description: string;
  /** 颜色（用于漏斗层渲染） */
  color: string;
  /** 行业 benchmark 转化率（参考值，用于对比） */
  benchmarkRate: number;
}

const FUNNEL_STAGES: FunnelStageDef[] = [
  {
    id: "exposure",
    label: "曝光",
    description: "内容被用户看到的次数，反映流量入口规模",
    color: "#38BDF8",
    benchmarkRate: 1.0,
  },
  {
    id: "click",
    label: "点击",
    description: "用户产生点赞/评论/转发等互动行为，反映内容吸引力",
    color: "#34D399",
    benchmarkRate: 0.08,
  },
  {
    id: "follow",
    label: "关注",
    description: "用户关注账号，反映账号粘性",
    color: "#FBBF24",
    benchmarkRate: 0.02,
  },
  {
    id: "interaction",
    label: "互动",
    description: "用户评论和私信，反映深度参与度",
    color: "#FB923C",
    benchmarkRate: 0.015,
  },
  {
    id: "conversion",
    label: "转化",
    description: "完成业务目标行为（估算为粉丝增长的 30%）",
    color: "#F87171",
    benchmarkRate: 0.006,
  },
  {
    id: "repurchase",
    label: "复购",
    description: "重复消费行为（估算为粉丝增长的 10%）",
    color: "#A78BFA",
    benchmarkRate: 0.002,
  },
];

const PLATFORM_OPTIONS: { value: MarketingPlatform | "all"; label: string }[] = [
  { value: "all", label: "全部平台聚合" },
  { value: "wechat_official", label: "微信公众号" },
  { value: "xiaohongshu", label: "小红书" },
  { value: "douyin_enterprise", label: "抖音企业号" },
  { value: "douyin_personal", label: "抖音个人号" },
  { value: "zhihu", label: "知乎" },
  { value: "bilibili", label: "B站" },
];

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: "day", label: "今日" },
  { value: "week", label: "本周" },
  { value: "month", label: "本月" },
];

/** 漏斗单层数据 */
interface FunnelLayerData {
  stage: FunnelStageDef;
  count: number;
  /** 相对上一层的转化率（首层为 1.0） */
  conversionRate: number;
  /** 流失人数（上一层 - 当前层） */
  dropped: number;
  /** 与行业 benchmark 的差距（百分点，负数表示低于 benchmark） */
  vsBenchmark: number;
}

/** AI 优化建议 */
interface OptimizationAdvice {
  /** 瓶颈环节 ID */
  bottleneckStageId: FunnelStageId;
  /** 瓶颈环节名称 */
  bottleneckLabel: string;
  /** 当前转化率 */
  currentRate: number;
  /** 行业 benchmark */
  benchmark: number;
  /** 差距百分比 */
  gap: number;
  /** 优化方向 */
  direction: string;
  /** 具体行动建议 */
  actions: string[];
}

/** A/B 测试状态 */
type ABTestStatus = "running" | "completed" | "paused";

/** A/B 测试 */
interface ABTest {
  id: string;
  name: string;
  description: string;
  variantA: {
    label: string;
    description: string;
    visitors: number;
    conversions: number;
  };
  variantB: {
    label: string;
    description: string;
    visitors: number;
    conversions: number;
  };
  status: ABTestStatus;
  /** 优胜版本（自动判定） */
  winner?: "A" | "B" | "tie";
  createdAt: string;
  updatedAt: string;
}

/** 漏斗自定义配置（用户可调） */
interface FunnelConfig {
  /** 转化阶段系数（默认 0.3） */
  conversionRate: number;
  /** 复购阶段系数（默认 0.1） */
  repurchaseRate: number;
}

const DEFAULT_FUNNEL_CONFIG: FunnelConfig = {
  conversionRate: 0.3,
  repurchaseRate: 0.1,
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 安全读取 localStorage JSON */
function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** 安全写入 localStorage JSON */
function saveJSON<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 容错：写入失败时静默忽略
  }
}

/** 格式化数字（千分位 + 万/亿单位） */
function formatNumber(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0";
  if (n >= 100000000) return `${(n / 100000000).toFixed(2)}亿`;
  if (n >= 10000) return `${(n / 10000).toFixed(2)}万`;
  return Math.round(n).toLocaleString();
}

/** 百分比格式化 */
function formatPercent(rate: number, digits = 2): string {
  if (!Number.isFinite(rate)) return "0.00%";
  return `${(rate * 100).toFixed(digits)}%`;
}

/**
 * 从 PlatformMetrics 推导漏斗各层数据
 * 注意：转化和复购是估算值（系数可由用户配置）
 */
function deriveFunnelCounts(
  metrics: {
    views: number;
    likes: number;
    comments: number;
    shares: number;
    favorites: number;
    dm: number;
    followersGain: number;
    followersTotal: number;
  },
  config: FunnelConfig
): Record<FunnelStageId, number> {
  const exposure = Math.max(0, metrics.views || 0);
  const click = Math.max(0, (metrics.likes || 0) + (metrics.comments || 0) + (metrics.shares || 0));
  const follow = Math.max(0, metrics.followersGain || 0);
  const interaction = Math.max(0, (metrics.comments || 0) + (metrics.dm || 0));
  // 转化 = 粉丝增长 * 转化系数（简化估算，标注来源）
  const conversion = Math.round(follow * config.conversionRate);
  // 复购 = 粉丝增长 * 复购系数（简化估算）
  const repurchase = Math.round(follow * config.repurchaseRate);
  return {
    exposure,
    click,
    follow,
    interaction,
    conversion,
    repurchase,
  };
}

/** 计算漏斗每层数据（含转化率/流失/对比 benchmark） */
function buildFunnelLayers(
  counts: Record<FunnelStageId, number>
): FunnelLayerData[] {
  const layers: FunnelLayerData[] = [];
  let prevCount = 0;
  for (let i = 0; i < FUNNEL_STAGES.length; i++) {
    const stage = FUNNEL_STAGES[i];
    const count = counts[stage.id] || 0;
    const conversionRate = i === 0 ? 1.0 : prevCount > 0 ? count / prevCount : 0;
    const dropped = i === 0 ? 0 : Math.max(0, prevCount - count);
    const vsBenchmark = conversionRate - stage.benchmarkRate;
    layers.push({ stage, count, conversionRate, dropped, vsBenchmark });
    prevCount = count;
  }
  return layers;
}

/** 识别瓶颈环节（最低转化率的非首层环节） */
function identifyBottleneck(layers: FunnelLayerData[]): FunnelLayerData | null {
  if (layers.length <= 1) return null;
  // 跳过首层（曝光），从第二层开始找最低转化率
  const candidates = layers.slice(1).filter((l) => l.count > 0);
  if (candidates.length === 0) return null;
  return candidates.reduce((min, cur) =>
    cur.conversionRate < min.conversionRate ? cur : min
  );
}

/** 基于瓶颈环节生成 AI 优化建议（内置规则，非假数据） */
function generateOptimizationAdvice(bottleneck: FunnelLayerData): OptimizationAdvice {
  const gap = bottleneck.conversionRate - bottleneck.stage.benchmarkRate;
  const adviceMap: Record<FunnelStageId, { direction: string; actions: string[] }> = {
    exposure: {
      direction: "扩大流量入口",
      actions: [
        "增加发布频率，覆盖更多发布时段",
        "优化标题与封面，提升点击率",
        "结合热点话题蹭流量",
        "投放付费推广扩大曝光",
      ],
    },
    click: {
      direction: "提升内容吸引力",
      actions: [
        "前 3 秒/前 3 行设置强钩子",
        "增加冲突与悬念，引发好奇",
        "优化内容结构，让用户愿意互动",
        "尝试不同内容形式（图文/视频）找到最佳形态",
      ],
    },
    follow: {
      direction: "增强账号粘性",
      actions: [
        "明确账号定位与人设，让用户有理由关注",
        "提供关注价值（独家福利/系列内容/干货合集）",
        "在内容结尾引导关注，给出明确理由",
        "建立系列内容，让用户期待后续更新",
      ],
    },
    interaction: {
      direction: "激发深度互动",
      actions: [
        "在内容中抛出争议话题引发讨论",
        "主动回复评论建立对话",
        "设置互动钩子（投票/问答/征集）",
        "私信提供专属价值引导用户主动联系",
      ],
    },
    conversion: {
      direction: "优化转化路径",
      actions: [
        "简化转化流程，减少步骤",
        "提供限时优惠制造紧迫感",
        "强化社会证明（用户见证/案例展示）",
        "在关键节点设置明确行动召唤",
      ],
    },
    repurchase: {
      direction: "建立复购机制",
      actions: [
        "建立会员体系提升复购激励",
        "定期推送复购提醒与新品信息",
        "提供老客户专属优惠",
        "收集复购反馈持续优化产品体验",
      ],
    },
  };
  const advice = adviceMap[bottleneck.stage.id];
  return {
    bottleneckStageId: bottleneck.stage.id,
    bottleneckLabel: bottleneck.stage.label,
    currentRate: bottleneck.conversionRate,
    benchmark: bottleneck.stage.benchmarkRate,
    gap,
    direction: advice.direction,
    actions: advice.actions,
  };
}

/** 自动判定 A/B 测试优胜版本（基于转化率与样本量） */
function judgeABTestWinner(test: ABTest): "A" | "B" | "tie" | null {
  if (test.variantA.visitors < 30 || test.variantB.visitors < 30) {
    // 样本不足，无法判定
    return null;
  }
  const rateA = test.variantA.conversions / test.variantA.visitors;
  const rateB = test.variantB.conversions / test.variantB.visitors;
  const diff = Math.abs(rateA - rateB);
  // 差距小于 1% 视为平局
  if (diff < 0.01) return "tie";
  return rateA > rateB ? "A" : "B";
}

// ============================================
// 主组件
// ============================================

export default function MarketingFunnel() {
  const dashboard = useDashboardData();
  const [platform, setPlatform] = useState<MarketingPlatform | "all">("all");
  const [timeRange, setTimeRange] = useState<TimeRange>("day");
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // 漏斗配置
  const [funnelConfig, setFunnelConfig] = useState<FunnelConfig>(() =>
    loadJSON<FunnelConfig>(FUNNEL_CONFIG_KEY, DEFAULT_FUNNEL_CONFIG)
  );
  const [showConfig, setShowConfig] = useState(false);

  // A/B 测试
  const [abTests, setAbTests] = useState<ABTest[]>(() =>
    loadJSON<ABTest[]>(AB_TESTS_KEY, [])
  );
  const [showAbModal, setShowAbModal] = useState(false);
  const [expandedTestId, setExpandedTestId] = useState<string | null>(null);

  // 持久化配置和测试
  useEffect(() => {
    saveJSON(FUNNEL_CONFIG_KEY, funnelConfig);
  }, [funnelConfig]);

  useEffect(() => {
    saveJSON(AB_TESTS_KEY, abTests);
  }, [abTests]);

  // 刷新数据
  const handleRefresh = useCallback(() => {
    setLoading(true);
    // 模拟加载（dashboard 数据来自 useDashboardData 内部缓存）
    setTimeout(() => {
      setLastRefresh(new Date());
      setLoading(false);
    }, 400);
  }, []);

  // 聚合所选平台的指标
  const aggregatedMetrics = useMemo(() => {
    const empty = {
      views: 0,
      likes: 0,
      comments: 0,
      shares: 0,
      favorites: 0,
      dm: 0,
      followersGain: 0,
      followersTotal: 0,
    };
    if (!dashboard?.marketing?.platforms) return empty;
    const platforms = dashboard.marketing.platforms;
    if (platform === "all") {
      // 聚合所有非降级平台
      const allKeys = Object.keys(platforms) as MarketingPlatform[];
      return allKeys.reduce((acc, key) => {
        const p = platforms[key];
        if (!p || p.degraded) return acc;
        const m = p.metrics || ({} as any);
        return {
          views: acc.views + (m.views || 0),
          likes: acc.likes + (m.likes || 0),
          comments: acc.comments + (m.comments || 0),
          shares: acc.shares + (m.shares || 0),
          favorites: acc.favorites + (m.favorites || 0),
          dm: acc.dm + (m.dm || 0),
          followersGain: acc.followersGain + (m.followersGain || 0),
          followersTotal: acc.followersTotal + (m.followersTotal || 0),
        };
      }, empty);
    }
    const p = platforms[platform];
    if (!p || p.degraded) return empty;
    const m = p.metrics || ({} as any);
    return {
      views: m.views || 0,
      likes: m.likes || 0,
      comments: m.comments || 0,
      shares: m.shares || 0,
      favorites: m.favorites || 0,
      dm: m.dm || 0,
      followersGain: m.followersGain || 0,
      followersTotal: m.followersTotal || 0,
    };
  }, [dashboard, platform]);

  // 是否有真实数据
  const hasRealData = useMemo(() => {
    return aggregatedMetrics.views > 0 || aggregatedMetrics.followersTotal > 0;
  }, [aggregatedMetrics]);

  // 漏斗各层数据
  const funnelCounts = useMemo(
    () => deriveFunnelCounts(aggregatedMetrics, funnelConfig),
    [aggregatedMetrics, funnelConfig]
  );

  // 漏斗层（含转化率/流失/对比 benchmark）
  const funnelLayers = useMemo(
    () => buildFunnelLayers(funnelCounts),
    [funnelCounts]
  );

  // 瓶颈环节
  const bottleneck = useMemo(
    () => identifyBottleneck(funnelLayers),
    [funnelLayers]
  );

  // AI 优化建议
  const advice = useMemo(
    () => (bottleneck ? generateOptimizationAdvice(bottleneck) : null),
    [bottleneck]
  );

  // 整体转化率（首层到末层）
  const overallConversionRate = useMemo(() => {
    const first = funnelLayers[0]?.count || 0;
    const last = funnelLayers[funnelLayers.length - 1]?.count || 0;
    return first > 0 ? last / first : 0;
  }, [funnelLayers]);

  // A/B 测试：创建新测试
  const handleCreateAbTest = useCallback(
    (test: Omit<ABTest, "id" | "createdAt" | "updatedAt" | "status" | "winner">) => {
      const now = new Date().toISOString();
      const newTest: ABTest = {
        ...test,
        id: genId("ab"),
        status: "running",
        createdAt: now,
        updatedAt: now,
      };
      setAbTests((prev) => [newTest, ...prev]);
      setShowAbModal(false);
    },
    []
  );

  // A/B 测试：更新版本数据
  const handleUpdateVariant = useCallback(
    (testId: string, variant: "A" | "B", field: "visitors" | "conversions", value: number) => {
      setAbTests((prev) =>
        prev.map((t) => {
          if (t.id !== testId) return t;
          const v = variant === "A" ? { ...t.variantA } : { ...t.variantB };
          v[field] = Math.max(0, value);
          const updated = variant === "A" ? { ...t, variantA: v } : { ...t, variantB: v };
          // 自动判定优胜
          const winner = judgeABTestWinner(updated);
          updated.winner = winner ?? undefined;
          updated.updatedAt = new Date().toISOString();
          return updated;
        })
      );
    },
    []
  );

  // A/B 测试：删除
  const handleDeleteAbTest = useCallback((testId: string) => {
    setAbTests((prev) => prev.filter((t) => t.id !== testId));
  }, []);

  // A/B 测试：切换状态
  const handleToggleAbTestStatus = useCallback((testId: string) => {
    setAbTests((prev) =>
      prev.map((t) => {
        if (t.id !== testId) return t;
        const next: ABTestStatus = t.status === "running" ? "paused" : "running";
        return { ...t, status: next, updatedAt: new Date().toISOString() };
      })
    );
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="space-y-3"
    >
      {/* Header */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
              <Filter size={14} className="text-pink-400" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-white tracking-tight">
                营销漏斗分析
              </h3>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                6 层漏斗 · 瓶颈识别 · AI 优化建议 · A/B 测试
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-gray-600">
              最后刷新：{lastRefresh.toLocaleTimeString("zh-CN")}
            </span>
            <button
              onClick={() => setShowConfig((v) => !v)}
              className="px-2 py-1 text-[10px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded transition-colors"
              title="漏斗配置"
            >
              配置
            </button>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-1.5 text-gray-400 hover:text-pink-400 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded transition-colors"
              title="刷新数据"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* 筛选条件 */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar size={11} className="text-gray-500" />
            <span className="text-[10px] font-mono text-gray-500 uppercase">时间范围</span>
            <div className="flex items-center gap-1">
              {TIME_RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setTimeRange(opt.value)}
                  className={`px-2 py-1 text-[10px] font-mono rounded transition-colors ${
                    timeRange === opt.value
                      ? "bg-pink-500/15 text-pink-300 border border-pink-500/30"
                      : "text-gray-500 hover:bg-white/[0.04] border border-transparent"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Target size={11} className="text-gray-500" />
            <span className="text-[10px] font-mono text-gray-500 uppercase">平台</span>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as MarketingPlatform | "all")}
              className="px-2 py-1 text-[10px] font-mono text-gray-300 bg-white/[0.03] border border-white/[0.06] rounded outline-none focus:border-pink-500/30"
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-[#0a0e1a]">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-[10px] font-mono text-gray-500 uppercase">整体转化率</span>
            <span className="text-[14px] font-mono font-bold text-emerald-400">
              {formatPercent(overallConversionRate)}
            </span>
          </div>
        </div>

        {/* 配置面板（可折叠） */}
        <AnimatePresence>
          {showConfig && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="overflow-hidden mt-3"
            >
              <div className="p-3 rounded border border-white/[0.06] bg-white/[0.01] space-y-2">
                <div className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                  漏斗配置（影响转化与复购阶段的估算系数）
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-[11px] font-mono text-gray-300">
                    <span className="text-gray-500">转化系数</span>
                    <input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={funnelConfig.conversionRate}
                      onChange={(e) =>
                        setFunnelConfig((prev) => ({
                          ...prev,
                          conversionRate: parseFloat(e.target.value) || 0,
                        }))
                      }
                      className="w-20 px-2 py-1 bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 outline-none focus:border-pink-500/30"
                    />
                    <span className="text-gray-600">（粉丝增长 × 此系数 = 转化数）</span>
                  </label>
                  <label className="flex items-center gap-2 text-[11px] font-mono text-gray-300">
                    <span className="text-gray-500">复购系数</span>
                    <input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={funnelConfig.repurchaseRate}
                      onChange={(e) =>
                        setFunnelConfig((prev) => ({
                          ...prev,
                          repurchaseRate: parseFloat(e.target.value) || 0,
                        }))
                      }
                      className="w-20 px-2 py-1 bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 outline-none focus:border-pink-500/30"
                    />
                    <span className="text-gray-600">（粉丝增长 × 此系数 = 复购数）</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setFunnelConfig(DEFAULT_FUNNEL_CONFIG)}
                    className="px-2 py-1 text-[10px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded transition-colors"
                  >
                    恢复默认
                  </button>
                  <span className="text-[10px] font-mono text-gray-600">
                    配置自动持久化到 localStorage
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 空状态 */}
      {!hasRealData && (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-8 text-center">
          <AlertTriangle size={20} className="text-gray-600 mx-auto mb-2" />
          <p className="text-[12px] font-mono text-gray-500">暂无营销数据</p>
          <p className="text-[10px] font-mono text-gray-600 mt-1">
            请检查 useDashboardData 中 marketing 数据是否已加载，或切换其他平台
          </p>
        </div>
      )}

      {/* 漏斗可视化 */}
      {hasRealData && (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingDown size={12} className="text-pink-400" />
              <span className="text-[11px] font-mono text-gray-300 uppercase tracking-wider">
                漏斗可视化
              </span>
              <span className="text-[10px] font-mono text-gray-600">
                倒金字塔 · 每层宽度按人数比例
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            {funnelLayers.map((layer, idx) => {
              // 每层宽度按相对首层的比例计算
              const firstCount = funnelLayers[0]?.count || 1;
              const widthPercent = Math.max(8, (layer.count / firstCount) * 100);
              const isBottleneck =
                bottleneck?.stage.id === layer.stage.id;
              return (
                <div
                  key={layer.stage.id}
                  className={`relative rounded border transition-all ${
                    isBottleneck
                      ? "border-red-500/40 bg-red-500/[0.04]"
                      : "border-white/[0.06] bg-white/[0.01]"
                  }`}
                  style={{
                    width: `${widthPercent}%`,
                    marginLeft: `${(100 - widthPercent) / 2}%`,
                  }}
                >
                  <div className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: layer.stage.color }}
                      />
                      <span className="text-[12px] font-medium text-white">
                        {layer.stage.label}
                      </span>
                      {isBottleneck && (
                        <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30">
                          瓶颈
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-mono">
                      <span className="text-gray-300">{formatNumber(layer.count)}</span>
                      {idx > 0 && (
                        <>
                          <span className="text-gray-500">→</span>
                          <span
                            className={`${
                              layer.vsBenchmark < 0
                                ? "text-red-400"
                                : "text-emerald-400"
                            }`}
                          >
                            {formatPercent(layer.conversionRate)}
                          </span>
                          <span className="text-gray-600">
                            流失 {formatNumber(layer.dropped)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {/* 阶段说明 */}
                  <div className="px-3 pb-2 text-[10px] font-mono text-gray-600">
                    {layer.stage.description}
                    {idx > 0 && (
                      <span className="ml-2 text-gray-700">
                        · 行业 benchmark {formatPercent(layer.stage.benchmarkRate)} ·
                        差距 {layer.vsBenchmark >= 0 ? "+" : ""}
                        {formatPercent(layer.vsBenchmark)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI 优化建议 */}
      {hasRealData && advice && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-lg border border-amber-500/20 bg-amber-500/[0.02] p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb size={14} className="text-amber-400" />
            <span className="text-[11px] font-mono text-amber-300 uppercase tracking-wider">
              AI 优化建议
            </span>
            <span className="text-[10px] font-mono text-gray-600">
              基于瓶颈环节分析
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="rounded border border-white/[0.06] bg-white/[0.01] px-3 py-2">
              <div className="text-[10px] font-mono text-gray-500 uppercase">瓶颈环节</div>
              <div className="text-[13px] font-mono text-red-400 mt-0.5">
                {advice.bottleneckLabel}
              </div>
            </div>
            <div className="rounded border border-white/[0.06] bg-white/[0.01] px-3 py-2">
              <div className="text-[10px] font-mono text-gray-500 uppercase">当前转化率</div>
              <div className="text-[13px] font-mono text-gray-200 mt-0.5">
                {formatPercent(advice.currentRate)}
              </div>
            </div>
            <div className="rounded border border-white/[0.06] bg-white/[0.01] px-3 py-2">
              <div className="text-[10px] font-mono text-gray-500 uppercase">行业 benchmark</div>
              <div className="text-[13px] font-mono text-gray-200 mt-0.5">
                {formatPercent(advice.benchmark)}
              </div>
            </div>
          </div>
          <div className="rounded border border-white/[0.06] bg-white/[0.01] p-3">
            <div className="text-[11px] font-mono text-gray-400 mb-2">
              优化方向：<span className="text-amber-300">{advice.direction}</span>
            </div>
            <div className="text-[10px] font-mono text-gray-500 mb-1.5">具体行动建议：</div>
            <ul className="space-y-1">
              {advice.actions.map((action, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[11px] font-mono text-gray-300"
                >
                  <span className="text-amber-400 mt-0.5">{i + 1}.</span>
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      )}

      {/* A/B 测试管理 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FlaskConical size={12} className="text-cyan-400" />
            <span className="text-[11px] font-mono text-gray-300 uppercase tracking-wider">
              A/B 测试管理
            </span>
            <span className="text-[10px] font-mono text-gray-600">
              共 {abTests.length} 个测试 · 运行中 {abTests.filter((t) => t.status === "running").length}
            </span>
          </div>
          <button
            onClick={() => setShowAbModal(true)}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded transition-colors"
          >
            <Plus size={10} />
            创建测试
          </button>
        </div>

        {abTests.length === 0 ? (
          <div className="py-6 text-center">
            <FlaskConical size={16} className="text-gray-600 mx-auto mb-2" />
            <p className="text-[11px] font-mono text-gray-500">暂无 A/B 测试</p>
            <p className="text-[10px] font-mono text-gray-600 mt-1">
              点击「创建测试」开始实验
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {abTests.map((test) => {
              const rateA =
                test.variantA.visitors > 0
                  ? test.variantA.conversions / test.variantA.visitors
                  : 0;
              const rateB =
                test.variantB.visitors > 0
                  ? test.variantB.conversions / test.variantB.visitors
                  : 0;
              const isExpanded = expandedTestId === test.id;
              return (
                <div
                  key={test.id}
                  className="rounded border border-white/[0.06] bg-white/[0.01] overflow-hidden"
                >
                  {/* 测试头部 */}
                  <button
                    onClick={() =>
                      setExpandedTestId(isExpanded ? null : test.id)
                    }
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown size={12} className="text-gray-400" />
                      ) : (
                        <ChevronRight size={12} className="text-gray-400" />
                      )}
                      <span className="text-[12px] font-medium text-white">
                        {test.name}
                      </span>
                      <span
                        className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${
                          test.status === "running"
                            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                            : test.status === "paused"
                            ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
                            : "bg-gray-500/10 text-gray-400 border-gray-500/30"
                        }`}
                      >
                        {test.status === "running"
                          ? "运行中"
                          : test.status === "paused"
                          ? "已暂停"
                          : "已完成"}
                      </span>
                      {test.winner && (
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                          优胜：{test.winner === "tie" ? "平局" : `版本 ${test.winner}`}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-mono text-gray-500">
                      <span>A: {formatPercent(rateA)}</span>
                      <span>B: {formatPercent(rateB)}</span>
                    </div>
                  </button>

                  {/* 展开内容 */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="border-t border-white/[0.06] overflow-hidden"
                      >
                        <div className="p-3 space-y-3">
                          <div className="text-[11px] font-mono text-gray-400">
                            {test.description}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {/* 版本 A */}
                            <div className="rounded border border-white/[0.06] bg-white/[0.01] p-2">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-[11px] font-mono text-cyan-300">
                                  版本 A · {test.variantA.label}
                                </span>
                                {test.winner === "A" && (
                                  <Award size={10} className="text-amber-400" />
                                )}
                              </div>
                              <div className="text-[10px] font-mono text-gray-500 mb-2">
                                {test.variantA.description}
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                <label className="text-[10px] font-mono text-gray-500">
                                  访客数
                                  <input
                                    type="number"
                                    min="0"
                                    value={test.variantA.visitors}
                                    onChange={(e) =>
                                      handleUpdateVariant(
                                        test.id,
                                        "A",
                                        "visitors",
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                    className="w-full mt-0.5 px-2 py-1 bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 outline-none focus:border-cyan-500/30"
                                  />
                                </label>
                                <label className="text-[10px] font-mono text-gray-500">
                                  转化数
                                  <input
                                    type="number"
                                    min="0"
                                    value={test.variantA.conversions}
                                    onChange={(e) =>
                                      handleUpdateVariant(
                                        test.id,
                                        "A",
                                        "conversions",
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                    className="w-full mt-0.5 px-2 py-1 bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 outline-none focus:border-cyan-500/30"
                                  />
                                </label>
                              </div>
                              <div className="mt-2 text-[11px] font-mono text-emerald-400">
                                转化率：{formatPercent(rateA)}
                              </div>
                            </div>

                            {/* 版本 B */}
                            <div className="rounded border border-white/[0.06] bg-white/[0.01] p-2">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-[11px] font-mono text-pink-300">
                                  版本 B · {test.variantB.label}
                                </span>
                                {test.winner === "B" && (
                                  <Award size={10} className="text-amber-400" />
                                )}
                              </div>
                              <div className="text-[10px] font-mono text-gray-500 mb-2">
                                {test.variantB.description}
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                <label className="text-[10px] font-mono text-gray-500">
                                  访客数
                                  <input
                                    type="number"
                                    min="0"
                                    value={test.variantB.visitors}
                                    onChange={(e) =>
                                      handleUpdateVariant(
                                        test.id,
                                        "B",
                                        "visitors",
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                    className="w-full mt-0.5 px-2 py-1 bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 outline-none focus:border-pink-500/30"
                                  />
                                </label>
                                <label className="text-[10px] font-mono text-gray-500">
                                  转化数
                                  <input
                                    type="number"
                                    min="0"
                                    value={test.variantB.conversions}
                                    onChange={(e) =>
                                      handleUpdateVariant(
                                        test.id,
                                        "B",
                                        "conversions",
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                    className="w-full mt-0.5 px-2 py-1 bg-white/[0.03] border border-white/[0.06] rounded text-gray-200 outline-none focus:border-pink-500/30"
                                  />
                                </label>
                              </div>
                              <div className="mt-2 text-[11px] font-mono text-emerald-400">
                                转化率：{formatPercent(rateB)}
                              </div>
                            </div>
                          </div>

                          {/* 操作按钮 */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleAbTestStatus(test.id)}
                              className="px-2 py-1 text-[10px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] rounded transition-colors"
                            >
                              {test.status === "running" ? "暂停" : "继续"}
                            </button>
                            <button
                              onClick={() => handleDeleteAbTest(test.id)}
                              className="px-2 py-1 text-[10px] font-mono text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded transition-colors flex items-center gap-1"
                            >
                              <Trash2 size={10} />
                              删除
                            </button>
                            <span className="text-[10px] font-mono text-gray-600 ml-auto">
                              创建于 {new Date(test.createdAt).toLocaleString("zh-CN")}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 创建 A/B 测试 Modal */}
      <AnimatePresence>
        {showAbModal && (
          <ABTestModal
            onClose={() => setShowAbModal(false)}
            onCreate={handleCreateAbTest}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：创建 A/B 测试 Modal
// ============================================

interface ABTestModalProps {
  onClose: () => void;
  onCreate: (test: Omit<ABTest, "id" | "createdAt" | "updatedAt" | "status" | "winner">) => void;
}

function ABTestModal({ onClose, onCreate }: ABTestModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [variantALabel, setVariantALabel] = useState("");
  const [variantADesc, setVariantADesc] = useState("");
  const [variantBLabel, setVariantBLabel] = useState("");
  const [variantBDesc, setVariantBDesc] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!name.trim()) {
      setError("请输入测试名称");
      return;
    }
    if (!variantALabel.trim() || !variantBLabel.trim()) {
      setError("请输入两个版本的名称");
      return;
    }
    onCreate({
      name: name.trim(),
      description: description.trim(),
      variantA: {
        label: variantALabel.trim(),
        description: variantADesc.trim(),
        visitors: 0,
        conversions: 0,
      },
      variantB: {
        label: variantBLabel.trim(),
        description: variantBDesc.trim(),
        visitors: 0,
        conversions: 0,
      },
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-xl border border-cyan-500/30 bg-[#0a0e1a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <FlaskConical size={14} className="text-cyan-400" />
            <h3 className="text-[14px] font-semibold text-white">创建 A/B 测试</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
          {error && (
            <div className="px-3 py-2 text-[11px] font-mono text-red-400 bg-red-500/10 border border-red-500/20 rounded">
              {error}
            </div>
          )}
          <label className="block">
            <span className="text-[11px] font-mono text-gray-400 uppercase">测试名称 *</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例：公众号标题 A/B 测试 - 2026年8月"
              className="w-full mt-1 px-3 py-2 text-[12px] text-gray-200 bg-white/[0.03] border border-white/[0.06] rounded outline-none focus:border-cyan-500/30"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-mono text-gray-400 uppercase">测试描述</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="说明测试目的和假设"
              rows={2}
              className="w-full mt-1 px-3 py-2 text-[12px] text-gray-200 bg-white/[0.03] border border-white/[0.06] rounded outline-none focus:border-cyan-500/30 resize-none"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            {/* 版本 A */}
            <div className="rounded border border-cyan-500/20 bg-cyan-500/[0.02] p-3 space-y-2">
              <div className="text-[11px] font-mono text-cyan-300 uppercase">版本 A</div>
              <label className="block">
                <span className="text-[10px] font-mono text-gray-500">名称 *</span>
                <input
                  type="text"
                  value={variantALabel}
                  onChange={(e) => setVariantALabel(e.target.value)}
                  placeholder="例：疑问式标题"
                  className="w-full mt-1 px-2 py-1.5 text-[11px] text-gray-200 bg-white/[0.03] border border-white/[0.06] rounded outline-none focus:border-cyan-500/30"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono text-gray-500">描述</span>
                <textarea
                  value={variantADesc}
                  onChange={(e) => setVariantADesc(e.target.value)}
                  placeholder="版本 A 的具体内容描述"
                  rows={3}
                  className="w-full mt-1 px-2 py-1.5 text-[11px] text-gray-200 bg-white/[0.03] border border-white/[0.06] rounded outline-none focus:border-cyan-500/30 resize-none"
                />
              </label>
            </div>
            {/* 版本 B */}
            <div className="rounded border border-pink-500/20 bg-pink-500/[0.02] p-3 space-y-2">
              <div className="text-[11px] font-mono text-pink-300 uppercase">版本 B</div>
              <label className="block">
                <span className="text-[10px] font-mono text-gray-500">名称 *</span>
                <input
                  type="text"
                  value={variantBLabel}
                  onChange={(e) => setVariantBLabel(e.target.value)}
                  placeholder="例：陈述式标题"
                  className="w-full mt-1 px-2 py-1.5 text-[11px] text-gray-200 bg-white/[0.03] border border-white/[0.06] rounded outline-none focus:border-pink-500/30"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono text-gray-500">描述</span>
                <textarea
                  value={variantBDesc}
                  onChange={(e) => setVariantBDesc(e.target.value)}
                  placeholder="版本 B 的具体内容描述"
                  rows={3}
                  className="w-full mt-1 px-2 py-1.5 text-[11px] text-gray-200 bg-white/[0.03] border border-white/[0.06] rounded outline-none focus:border-pink-500/30 resize-none"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-[11px] font-mono text-gray-400 hover:text-gray-300 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="px-3 py-1.5 text-[11px] font-mono text-cyan-300 bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 rounded transition-colors"
          >
            创建测试
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
